export const DEFAULT_INTENSITY_OPTIONS = [20, 30, 40, 50, 60, 70];
export const DEFAULT_SELECTED_INTENSITIES = [20, 30, 40, 50];
export const DEFAULT_FREQUENCIES = [500, 1000, 2000, 4000, 8000, 16000];
export const DEFAULT_PHONE_LISTENING_FREQUENCIES = [500, 1000, 2000, 4000];

export const sortNumbers = (values = []) =>
  [...new Set(values.map(Number).filter((value) => !isNaN(value)))].sort(
    (a, b) => a - b,
  );

export const normalizeAvailableFrequencies = (availableFrequencies = []) => {
  const frequencies = sortNumbers(availableFrequencies);
  return frequencies.length ? frequencies : DEFAULT_FREQUENCIES;
};

export const getDefaultSelectedFrequencies = (
  availableFrequencies,
  isBluetoothSpeaker,
) => {
  const normalizedAvailableFrequencies = normalizeAvailableFrequencies(
    availableFrequencies,
  );

  if (isBluetoothSpeaker) {
    return normalizedAvailableFrequencies;
  }

  return DEFAULT_PHONE_LISTENING_FREQUENCIES.filter((frequency) =>
    normalizedAvailableFrequencies.includes(frequency),
  );
};

export const buildNoStimulusState = (frequencies, previous = {}) =>
  frequencies.reduce((acc, frequency) => {
    acc[frequency] =
      previous?.[frequency] ?? previous?.[String(frequency)] ?? "";
    return acc;
  }, {});

export const buildTableState = (frequencies, intensities, previous = {}) =>
  frequencies.reduce((acc, frequency) => {
    acc[frequency] = intensities.reduce((rowAcc, intensity) => {
      const previousRow =
        previous?.[frequency] ?? previous?.[String(frequency)] ?? {};
      rowAcc[intensity] =
        previousRow?.[intensity] ?? previousRow?.[String(intensity)] ?? "";
      return rowAcc;
    }, {});
    return acc;
  }, {});

export const getDefaultTitle = (modelName) =>
  modelName?.trim() ? `Odsłuch ${modelName}` : "Odsłuch";

export const getInitialFormState = ({
  availableFrequencies,
  defaultSelectedFrequencies,
  defaultDeviceId,
  defaultModelName,
  isByModel,
}) => {
  const normalizedFrequencies = normalizeAvailableFrequencies(
    availableFrequencies,
  );
  const normalizedSelectedFrequencies = sortNumbers(
    defaultSelectedFrequencies,
  ).filter((frequency) => normalizedFrequencies.includes(frequency));
  const selectedFrequencies = normalizedSelectedFrequencies.length
    ? normalizedSelectedFrequencies
    : normalizedFrequencies;

  return {
    title: "",
    description: "",
    location: "",
    deviceId: defaultDeviceId || "",
    selectedFrequencies,
    selectedIntensities: DEFAULT_SELECTED_INTENSITIES,
    noStimulusByFrequency: buildNoStimulusState(selectedFrequencies),
    generalBackgroundIntensity: "",
    sonometerError: "",
    tableValues: buildTableState(
      selectedFrequencies,
      DEFAULT_SELECTED_INTENSITIES,
    ),
    defaultGeneratedTitle: getDefaultTitle(defaultModelName),
    isByModel,
  };
};

export const buildEditFormState = (listening) => {
  const selectedFrequencies = sortNumbers(listening?.frequencies || []);
  const selectedIntensities = sortNumbers(listening?.intensities || []);

  const noStimulusByFrequency =
    listening?.listeningConditions?.noStimulusByFrequency || {};
  const tableValues =
    listening?.listeningTable?.valuesByFrequency ||
    listening?.listeningTable ||
    {};

  return {
    title: listening?.title || "",
    description: listening?.description || "",
    location: listening?.location || "",
    deviceId: listening?.deviceId || "",
    selectedFrequencies,
    selectedIntensities,
    noStimulusByFrequency: buildNoStimulusState(
      selectedFrequencies,
      noStimulusByFrequency,
    ),
    generalBackgroundIntensity:
      listening?.listeningConditions?.generalBackgroundIntensity ?? "",
    sonometerError: listening?.listeningConditions?.sonometerError ?? "",
    tableValues: buildTableState(
      selectedFrequencies,
      selectedIntensities,
      tableValues,
    ),
    defaultGeneratedTitle: listening?.title || "Odsłuch",
    isByModel: listening?.averagingLevel === "BY_MODEL",
  };
};

export const rebuildFormAfterFrequencyToggle = (form, frequency) => {
  const alreadySelected = form.selectedFrequencies.includes(frequency);
  const nextSelectedFrequencies = alreadySelected
    ? form.selectedFrequencies.filter((item) => item !== frequency)
    : [...form.selectedFrequencies, frequency];

  const sortedFrequencies = sortNumbers(nextSelectedFrequencies);

  return {
    ...form,
    selectedFrequencies: sortedFrequencies,
    noStimulusByFrequency: buildNoStimulusState(
      sortedFrequencies,
      form.noStimulusByFrequency,
    ),
    tableValues: buildTableState(
      sortedFrequencies,
      form.selectedIntensities,
      form.tableValues,
    ),
  };
};

export const rebuildFormAfterIntensityToggle = (form, intensity) => {
  const alreadySelected = form.selectedIntensities.includes(intensity);
  const nextSelectedIntensities = alreadySelected
    ? form.selectedIntensities.filter((item) => item !== intensity)
    : [...form.selectedIntensities, intensity];

  const sortedIntensities = sortNumbers(nextSelectedIntensities);

  return {
    ...form,
    selectedIntensities: sortedIntensities,
    tableValues: buildTableState(
      form.selectedFrequencies,
      sortedIntensities,
      form.tableValues,
    ),
  };
};

export const buildTitleSuffix = ({
  brandName,
  modelName,
  deviceId,
  includeDeviceId,
}) => {
  const parts = [];

  if (brandName) {
    parts.push(brandName);
  }

  if (modelName) {
    parts.push(modelName);
  }

  if (includeDeviceId && deviceId) {
    parts.push(deviceId);
  }

  return parts.join(" - ");
};

export const getListeningsDialogTitle = ({
  isByModel,
  defaultBrandName,
  defaultModelName,
  defaultDeviceId,
}) => {
  const suffix = buildTitleSuffix({
    brandName: defaultBrandName,
    modelName: defaultModelName,
    deviceId: defaultDeviceId,
    includeDeviceId: !isByModel,
  });

  return suffix ? `Odsłuchy — ${suffix}` : "Odsłuchy";
};

export const getListeningDetailsDialogTitle = ({
  defaultBrandName,
  defaultModelName,
  listening,
}) => {
  const suffix = buildTitleSuffix({
    brandName: defaultBrandName,
    modelName: defaultModelName,
    deviceId: listening?.deviceId || "",
    includeDeviceId: true,
  });

  return suffix ? `Szczegóły odsłuchu — ${suffix}` : "Szczegóły odsłuchu";
};

const buildListeningConditionsPayload = (form, sortedFrequencies) => ({
  noStimulusByFrequency: sortedFrequencies.reduce((acc, frequency) => {
    acc[frequency] = Number(form.noStimulusByFrequency[frequency]);
    return acc;
  }, {}),
  generalBackgroundIntensity: Number(form.generalBackgroundIntensity),
  sonometerError: Number(form.sonometerError),
});

const buildListeningTablePayload = (
  form,
  sortedFrequencies,
  sortedIntensities,
) => ({
  valuesByFrequency: sortedFrequencies.reduce((acc, frequency) => {
    acc[frequency] = sortedIntensities.reduce((rowAcc, intensity) => {
      const rawValue = form.tableValues?.[frequency]?.[intensity];

      if (rawValue !== "" && rawValue != null) {
        rowAcc[intensity] = Number(rawValue);
      }

      return rowAcc;
    }, {});
    return acc;
  }, {}),
});

export const buildCreateRequestBody = ({
  form,
  averagingLevel,
  defaultModelName,
}) => {
  const sortedFrequencies = sortNumbers(form.selectedFrequencies);
  const sortedIntensities = sortNumbers(form.selectedIntensities);

  return {
    averagingLevel: averagingLevel || null,
    deviceId: form.deviceId.trim(),
    listening: {
      title: form.title.trim() || getDefaultTitle(defaultModelName),
      description: form.description,
      location: form.location.trim(),
      frequencies: sortedFrequencies,
      intensities: sortedIntensities,
      listeningConditions: buildListeningConditionsPayload(
        form,
        sortedFrequencies,
      ),
      listeningTable: buildListeningTablePayload(
        form,
        sortedFrequencies,
        sortedIntensities,
      ),
    },
  };
};

export const buildUpdateRequestBody = ({ form, defaultModelName }) => {
  const sortedFrequencies = sortNumbers(form.selectedFrequencies);
  const sortedIntensities = sortNumbers(form.selectedIntensities);

  const body = {
    title: form.title.trim() || getDefaultTitle(defaultModelName),
    description: form.description,
    location: form.location.trim(),
    frequencies: sortedFrequencies,
    intensities: sortedIntensities,
    listeningConditions: buildListeningConditionsPayload(
      form,
      sortedFrequencies,
    ),
    listeningTable: buildListeningTablePayload(
      form,
      sortedFrequencies,
      sortedIntensities,
    ),
  };

  if (form.isByModel) {
    body.deviceId = form.deviceId.trim();
  }

  return body;
};
